import { AbstractControl, FormControl, Validators, FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import { User } from './user.schema';
import { Delivery, toDate } from './delivery.schema';
import { phoneValid } from '../helpers/validators/phone.validator';
import { ServiceModel } from './service.schems';
import { Transform } from 'class-transformer';
import { dateRangeValidator } from '../helpers/validators/date-range.validator';
import { EnumAux } from '../helpers/enum-aux.helper';
import { sdiOrCertifiedEmailRequiredValidator } from '../helpers/validators/sdi-certified-email.validator';

export enum PartnerPaymentMethods {
    bankTransfer = "bankTransfer",
    creditCard = "creditCard",
    directDebitMandate = "directDebitMandate"
}

export enum PartnerPaymentStatus {
    active = "active",
    inactive = "inactive",
    blocked = "blocked"
}

export enum DeliveryCodeCheckType {
    //uniqueCode = "uniqueCode",
    //none = "none",
    UNIQUE_PER_CUSTOMER = 'UNIQUE_PER_CUSTOMER',
    UNIQUE_PER_DELIVERY = 'UNIQUE_PER_DELIVERY',
}

export class DeliveryCodeCheckTypeStatusAux extends EnumAux {
    static codecheckstatus = ["CUSTOMER", "DELIVERY"];

    static aux = {
        UNIQUE_PER_CUSTOMER: { css: "red", ln: { it: "UNIVOCO PER CLIENTE" } },
        UNIQUE_PER_DELIVERY: { css: "yellow", ln: { it: "UNIVOCO PER CONSEGNA" } },
    }
}

export const VAT_OR_CF_PATTERN = /^(\d{11}|[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z])$/i;
export const DECIMAL_PATTERN = /^\d+(\.\d{1,2})?$/;

export class Partner {
    public static validation(profile = false): { [key: string]: AbstractControl } {
        const decimalPattern = /^\d+(\.\d{1,2})?$/;
        // VAT ID: allow Italian VAT (11 digits) or VIES style with ISO country code (2 letters + 11 chars = 13 total).
        const vatCodePattern = /^(?:\d{11}|[A-Za-z]{2}[A-Za-z0-9]{11})$/;
        // Tax ID: allow Italian personal CF (16), Italian company CF (11 digits), or generic EU style (country code + local identifier).
        const fiscalCodePattern = /^(?:[A-Za-z]{6}\d{2}[A-Za-z]\d{2}[A-Za-z]\d{3}[A-Za-z]|\d{11}|[A-Za-z]{2}[A-Za-z0-9]{8,14})$/;

        let validator: { [key: string]: AbstractControl } = {
            businessName: new FormControl('', Validators.required),
            vatCode: new FormControl(''),
            fiscalCode: new FormControl(''),
            phone: new FormControl('', [Validators.required, phoneValid()]),
            address: new FormControl('', Validators.required),
            longitude: new FormControl(''),
            latitude: new FormControl(''),
            notes: new FormControl(''),
            sendSms: new FormControl(false),
            kmIncluded: new FormControl(null, [Validators.pattern(decimalPattern)]),
            partnerServices: new FormArray([new FormGroup(PartnerServices.validation())]),
            partnerProvinces: new FormArray([new FormGroup(PartnerProvinces.validation())]),
            partnerSoldProductCategories: new FormControl([]),
            receiveEmailMsg: new FormControl(true),
            receiveWhatsappMsg: new FormControl(false),
            extraOutSideCityKmPrice: new FormControl(0, [Validators.pattern(DECIMAL_PATTERN)]),
            billingAccess: new FormControl(true),
            billingEmail: new FormControl('', [Validators.pattern(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)]),
            agency: new FormControl(''),
            partnerPaymentStatus: new FormControl(null),
            partnerPaymentMethod: new FormControl(null),
            bankAccount: new FormControl(''),
            bankAccountName: new FormControl(''),
            sdiCode: new FormControl('', [
                Validators.minLength(7),
                Validators.maxLength(7),
                Validators.pattern(/^[A-Za-z0-9]{7}$/),
                sdiOrCertifiedEmailRequiredValidator('certifiedEmail'),
            ]),
            contractValidity: new FormGroup({
                startContractDate: new FormControl(''),
                endContractDate: new FormControl(''),
            }, { validators: [dateRangeValidator] }),
            partnerShopUrl: new FormControl(''),
            checkExpertIndentity: new FormControl(false),
            deliveryCodeCheck: new FormControl(false),
            deliveryCodeCheckType: new FormControl(''),
            partnerHasWarehouse: new FormControl(false),
            isMultiplePickUpAddress: new FormControl(false),
            pickupAddresses: new FormArray([]),
            wooCommerceApiKey: new FormControl(null),
            certifiedEmail: new FormControl('', [
                Validators.pattern(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/),
                sdiOrCertifiedEmailRequiredValidator('sdiCode'),
            ]),
            activityReminder: new FormControl(true),
        };
        validator['user'] = profile ?
            new FormGroup({ ...User.specificValidation(), ...User.editLoginValidation() }) :
            new FormGroup(User.specificValidation());
        validator['vatCode'].setValidators([
            // Validators.pattern(/^\d{11}$/),
             Validators.pattern(VAT_OR_CF_PATTERN),
            // requiredIfAllEmpty({ value: [validator['fiscalCode']] })
        ]);
        validator['fiscalCode'].setValidators([
            // Validators.pattern(/^\w{6}\d{2}\w\d{2}\w\d{3}\w$/),
            Validators.pattern(VAT_OR_CF_PATTERN),
            // requiredIfAllEmpty({ value: [validator['vatCode']] })
        ]);

        return validator;
    }

    public static removeError(control: AbstractControl | null, key: string) {
        if (!control) return;

        if (control.hasError(key)) {
            const errors = { ...control.errors };
            delete errors[key];
            control.setErrors(Object.keys(errors).length ? errors : null);
        }
    }

    public static readonly validationMessages = {
        'businessName': [
            { type: 'required', message: 'Obbligatorio' }
        ],
        'phone': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'pattern', message: 'Deve essere un numero valido' },
            { type: 'phone', message: 'Formato invalido. Deve contenere fra le 10 e le 14 cifre' }
        ],
        'address': [
            { type: 'required', message: 'Obbligatorio' }
        ],
        'user': User.validationMessages,
        'vatCode': [
            { type: 'required', message: 'Obbligatoria' },
            { type: 'pattern', message: 'Deve essere un numero valido' },
            { type: 'requiredIfAllEmpty', message: 'Codice fiscale o partita iva obbligatori' },
            { type: 'invalidVat', message: 'Partita IVA non valida' },
            { type: 'vatServiceDown', message: 'Servizio verifica non disponibile' },
        ],
        'fiscalCode': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'pattern', message: 'Deve essere un numero valido' },
            { type: 'requiredIfAllEmpty', message: 'Codice fiscale o partita iva obbligatori' }
        ],
        'partnerServices': {
            'service': {
                'id': [
                    { type: 'required', message: 'Obbligatorio' }
                ],
                'price': [
                    { type: 'pattern', message: 'Deve essere un numero valido' }
                ],
                'extraKmPrice': [
                    { type: 'pattern', message: 'Deve essere un numero valido' }
                ],
                'pricePerItem': [
                    { type: 'pattern', message: 'Deve essere un numero valido' }
                ],
                'extraOutSideCityKmPrice': [
                    { type: 'pattern', message: 'Deve essere un numero valido' }
                ]
            },
        },
        'partnerSoldProductCategories': [
            { type: 'required', message: 'Obbligatorio' }
        ],
        'extraOutSideCityKmPrice': [
            { type: 'pattern', message: 'Deve essere un numero valido' }
        ],
        'kmIncluded': [
            { type: 'pattern', message: 'Deve essere un numero valido' }
        ],
        'billingEmail': [
            { type: 'pattern', message: 'Formato errato' }
        ],
        'partnerPaymentMethod': [
            { type: 'required', message: 'Obbligatorio' }
        ],
        'partnerPaymentStatus': [
            { type: 'required', message: 'Obbligatorio' }
        ],
        'bankAccount': [
            { type: 'required', message: 'Obbligatorio' }
        ],
        'bankAccountName': [
            { type: 'required', message: 'Obbligatorio' }
        ],
        'sdiCode': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'minlength', message: 'Il codice SDI deve contenere esattamente 7 caratteri' },
            { type: 'maxlength', message: 'Il codice SDI deve contenere esattamente 7 caratteri' },
            { type: 'pattern', message: 'Formato errato. Solo lettere e numeri sono ammessi' },
            { type: 'sdiOrCertifiedEmailRequired', message: 'Obbligatorio' },
        ],
        'contractValidity': [
            { type: 'startRequired', message: 'Inserire la data di inizio.' },
            { type: 'endRequired', message: 'Inserire la data di fine.' },
            { type: 'invalidRange', message: 'La data di fine deve essere successiva alla data di inizio.' }
        ],
        'deliveryCodeCheckType': [
            { type: 'required', message: 'Obbligatorio' },
        ],
        'pickupAddresses': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'invalidAddress', message: 'Indirizzo non valido. Si prega di selezionare un indirizzo valido dall\'elenco.' }
        ],
        'hideCustomerServices': [
            {
                type: 'required',
                message: 'Seleziona almeno un servizio',
            },
        ],
        'certifiedEmail': [
            { type: 'pattern', message: 'Formato errato' },
            { type: 'sdiOrCertifiedEmailRequired', message: 'Obbligatorio' },
        ],
    };

    public static serviceFormGroup() {
        return new FormGroup(PartnerServices.validation());
    }

    public static provinceFormGroup() {
        return new FormGroup(PartnerProvinces.validation());
    }

    constructor(
        public id?: number,
        public businessName?: string,
        public vatCode?: string,
        public fiscalCode?: string,
        public phone?: string,
        public address?: string,
        public longitude?: string,
        public latitude?: string,
        public notes?: string,
        public user?: User,
        public deliveries?: Delivery[],
        public sendSms?: boolean,
        public partnerServices?: PartnerServices[],
        public partnerProvinces?: PartnerProvinces[],
        public kmIncluded?: number,
        public partnerSoldProductCategories?: any,
        public receiveEmailMsg?: boolean,
        public receiveWhatsappMsg?: boolean,
        public extraOutSideCityKmPrice?: number,
        public billingAccess?: boolean,
        public billingEmail?: string,
        public agency?: string,
        public partnerPaymentStatus?: PartnerPaymentStatus,
        public partnerPaymentMethod?: PartnerPaymentMethods,
        public bankAccount?: string,
        public bankAccountName?: string,
        public sdiCode?: string,
        public contractValidity?: any,
        public stripeCustomer?: any,
        public saleImage?: string,
        startContractDate?: Date,
        endContractDate?: Date,
        public partnerShopUrl?: string,
        public checkExpertIndentity?: boolean,
        public deliveryCodeCheck?: boolean,
        public deliveryCodeCheckType?: DeliveryCodeCheckType,
        public partnerHasWarehouse?: boolean,
        public isMultiplePickUpAddress?: boolean,
        public pickupAddresses?: string[],
        public wooCommerceApiKey?: string,
        public certifiedEmail?: any,
        public activityReminder?: boolean,
    ) { 
        this.startContractDate = startContractDate;
        this.endContractDate = endContractDate;
    }

    @Transform(toDate, { toClassOnly: true }) public startContractDate?: Date;
    @Transform(toDate, { toClassOnly: true }) public endContractDate?: Date;
}

export function vatOrFiscalRequiredValidator(group: FormGroup): ValidationErrors | null {
  const vatControl = group.get('vatCode');
  const fiscalControl = group.get('fiscalCode');

  const vat = vatControl?.value?.trim();
  const fiscal = fiscalControl?.value?.trim();

  if (!vat && !fiscal) {
    vatControl?.setErrors({
      ...vatControl.errors,
      requiredIfAllEmpty: true
    });

    fiscalControl?.setErrors({
      ...fiscalControl.errors,
      requiredIfAllEmpty: true
    });

    return { requiredIfAllEmpty: true };
  }

  Partner.removeError(vatControl, 'requiredIfAllEmpty');
  Partner.removeError(fiscalControl, 'requiredIfAllEmpty');

  return null;
}

export class PartnerServices {
    public static validation(): { [key: string]: AbstractControl } {
        let validator: { [key: string]: AbstractControl } = {
            service: this.serviceFormGroup(),
        };
        return validator;
    }

    public static serviceFormGroup() {
        const decimalPattern = /^\d+(\.\d{1,2})?$/;
        return new FormGroup({
            id: new FormControl(null),
            price: new FormControl(null, [Validators.pattern(DECIMAL_PATTERN)]),
            extraKmPrice: new FormControl(null, [Validators.pattern(DECIMAL_PATTERN)]),
            pricePerItem: new FormControl(null, [Validators.pattern(DECIMAL_PATTERN)]),
            // extraOutSideCityKmPrice: new FormControl(null, [Validators.pattern(DECIMAL_PATTERN)]),
        });
    }

    constructor(
        public price?: string,
        public extraKmPrice?: string,
        public pricePerItem?: string,
        // public extraOutSideCityKmPrice?: number,
        public service?: ServiceModel,
        public updatedAt?: Date,
        public createdAt?: Date,
    ) { }
}
export class PartnerProvinces {
    public static validation(): { [key: string]: AbstractControl } {
        let validator: { [key: string]: AbstractControl } = {
            province: this.provinceFormGroup(),
        };
        return validator;
    }

    public static provinceFormGroup() {
        return new FormGroup({
            id: new FormControl(null),
        });
    }

    constructor(
        public id?: number,
        public provinceCode?: string,
        public province?: any,
        public updatedAt?: Date,
        public createdAt?: Date,
    ) { }
}